import { useEffect, useState, useCallback } from "react";
import { gql, useLazyQuery, DocumentNode } from "@apollo/client";
import { CancerStage } from "@app/provider/types";
import { TestCardStatus } from "@app/provider/testData/types";

/**
 * Counseling intent label derived from the selected order's test status.
 * Used by the GIS intake flow to differentiate pre-test vs post-test
 * counseling sessions when surfaced to Acuity.
 */
export type CounselingIntent = "pre-test" | "post-test";

/**
 * Default counseling intent used when the order's test status is not yet
 * known (e.g. test card details still loading, deep link without a status,
 * or an unrecognized status from a future backend release). "pre-test" is
 * the safer default because it routes through the patient-education step
 * before scheduling rather than skipping it.
 */
export const DEFAULT_COUNSELING_INTENT: CounselingIntent = "pre-test";

/**
 * Classifies the counseling intent for a GIS session based on the order's
 * test card status:
 *   - `RESULT_READY` or `TEST_COMPLETE` → "post-test"
 *   - all other statuses (including unknown/null) → DEFAULT_COUNSELING_INTENT
 *     ("pre-test")
 *
 * Statuses are sourced from `TestCardStatus`. Accepts a plain string for
 * compatibility with backend payloads that haven't yet been narrowed to the
 * enum (see server/src/graphql/types/testCard.ts).
 *
 * Consumers that have a possibly-null counseling intent from `IntakeFormContext`
 * can fall back to the default with `intent ?? DEFAULT_COUNSELING_INTENT`.
 */
export function classifyCounselingIntent(
  status: TestCardStatus | string | null | undefined
): CounselingIntent {
  if (
    status === TestCardStatus.RESULT_READY ||
    status === TestCardStatus.TEST_COMPLETE
  ) {
    return "post-test";
  }
  return DEFAULT_COUNSELING_INTENT;
}

/**
 * Named steps in the GIS intake form flow.
 * Using an enum rather than numeric indices so steps can be reordered
 * or added without requiring a data migration.
 */
export enum GisIntakeStep {
  CONFIRM_INFO = "CONFIRM_INFO",
  CLINIC_INFO = "CLINIC_INFO",
  SELECT_TEST = "SELECT_TEST",
  ACTIVE_DISEASE = "ACTIVE_DISEASE",
  CANCER_TYPE = "CANCER_TYPE",
  CANCER_STAGE = "CANCER_STAGE",
  REASON = "REASON",
  UNDERSTANDING = "UNDERSTANDING",
  PREFERENCE = "PREFERENCE",
  DOCTOR_APPOINTMENT = "DOCTOR_APPOINTMENT",
  LOCATION_INTERPRETER = "LOCATION_INTERPRETER",
  CONSENT = "CONSENT",
}

/**
 * Ordered list of steps for the standard, order-backed intake flow — the
 * single source of truth for step ordering. Reorder this array to change the
 * flow without touching individual components.
 */
export const GIS_INTAKE_STEPS: GisIntakeStep[] = [
  GisIntakeStep.CONFIRM_INFO,
  GisIntakeStep.ACTIVE_DISEASE,
  GisIntakeStep.REASON,
  GisIntakeStep.DOCTOR_APPOINTMENT,
  GisIntakeStep.LOCATION_INTERPRETER,
  GisIntakeStep.UNDERSTANDING,
  GisIntakeStep.PREFERENCE,
  GisIntakeStep.CONSENT,
];

/**
 * Ordered list of steps for the no-order ("no test found") intake flow. The
 * patient has no backing order, so the test type, cancer type, and cancer
 * stage that the standard flow pre-fills from the order are instead collected
 * up-front via dedicated steps.
 */
export const GIS_INTAKE_STEPS_NO_ORDER: GisIntakeStep[] = [
  GisIntakeStep.CONFIRM_INFO,
  GisIntakeStep.CLINIC_INFO,
  GisIntakeStep.SELECT_TEST,
  GisIntakeStep.CANCER_TYPE,
  GisIntakeStep.CANCER_STAGE,
  GisIntakeStep.ACTIVE_DISEASE,
  GisIntakeStep.REASON,
  GisIntakeStep.DOCTOR_APPOINTMENT,
  GisIntakeStep.LOCATION_INTERPRETER,
  GisIntakeStep.UNDERSTANDING,
  GisIntakeStep.PREFERENCE,
  GisIntakeStep.CONSENT,
];

/** Guest flows use the same order-backed vs no-order step distinction. */
export const GIS_GUEST_INTAKE_STEPS: GisIntakeStep[] = [...GIS_INTAKE_STEPS];

export const GIS_GUEST_INTAKE_STEPS_NO_ORDER: GisIntakeStep[] = [
  ...GIS_INTAKE_STEPS_NO_ORDER,
];

export const TOTAL_INTAKE_STEPS = GIS_INTAKE_STEPS.length;

/** Maps each step enum to its route suffix (appended to the intake base path). */
export const STEP_ROUTE_SUFFIX: Record<GisIntakeStep, string> = {
  [GisIntakeStep.CONFIRM_INFO]: "",
  [GisIntakeStep.CLINIC_INFO]: "/clinic-info",
  [GisIntakeStep.SELECT_TEST]: "/select-test",
  [GisIntakeStep.ACTIVE_DISEASE]: "/active-disease",
  [GisIntakeStep.CANCER_TYPE]: "/cancer-type",
  [GisIntakeStep.CANCER_STAGE]: "/cancer-stage",
  [GisIntakeStep.REASON]: "/reason",
  [GisIntakeStep.UNDERSTANDING]: "/understanding",
  [GisIntakeStep.PREFERENCE]: "/preference",
  [GisIntakeStep.DOCTOR_APPOINTMENT]: "/doctor-appointment",
  [GisIntakeStep.LOCATION_INTERPRETER]: "/location-interpreter",
  [GisIntakeStep.CONSENT]: "/consent",
};

/**
 * Returns the 1-based display number for a step (for progress bar and
 * "Step X of Y"), within the provided step sequence.
 */
export function getStepNumber(
  step: GisIntakeStep,
  steps: GisIntakeStep[] = GIS_INTAKE_STEPS
): number {
  return steps.indexOf(step) + 1;
}

/** Returns the next step in the flow, or null if this is the last step. */
export function getNextStep(
  step: GisIntakeStep,
  steps: GisIntakeStep[] = GIS_INTAKE_STEPS
): GisIntakeStep | null {
  const idx = steps.indexOf(step);
  return idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null;
}

/** Returns the previous step in the flow, or null if this is the first step. */
export function getPreviousStep(
  step: GisIntakeStep,
  steps: GisIntakeStep[] = GIS_INTAKE_STEPS
): GisIntakeStep | null {
  const idx = steps.indexOf(step);
  return idx > 0 ? steps[idx - 1] : null;
}

/**
 * Represents the pre-filled data available for the GIS intake form.
 * Fields marked as null require user input during the form flow.
 */
export interface IntakeFormData {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  phone: string | null;
  email: string | null;
  zipCode?: string | null;
  physicianClinic: string | null;
  orderingPhysician: string | null;
  cancerType: string | null;
  cancerStage: CancerStage | null;
  testType: string | null;
  hasGISAppointment: boolean | null;
  hasPreviouslyTakenTest: boolean | null;
}

/**
 * User-provided fields that cannot be pre-filled from existing data.
 * These require explicit user input during the intake form flow.
 */
export interface IntakeFormUserInputFields {
  hasActiveDisease: boolean | null;
  reasonForAppointment: string | null;
  easeOfUnderstandingInformation: string | null;
  wantToTalkToGeneticCounselorImmediately: boolean | null;
  alreadyScheduledAppointmentWithDoctor: boolean | null;
  countryOfResidence: string | null;
  interpreterNeeded: boolean | null;
  interpreterLanguage: string | null;
  consentAcknowledged: boolean | null;
}

/**
 * Complete intake form data combining pre-filled and user-provided fields.
 */
export interface CompleteIntakeFormData
  extends IntakeFormData, IntakeFormUserInputFields {}

/**
 * Response type for the GIS intake form data query
 */
export interface GisIntakeFormDataResponse {
  getGisIntakeFormData: IntakeFormData;
}

export interface TriageRuleMatch {
  ruleId: string;
  description: string;
}

export interface TriageResult {
  gisFlowType: string;
  skipPatientEducation: boolean;
  matchedRules: TriageRuleMatch[];
}

/**
 * Shape returned by session-related mutations / queries.
 */
export interface OncUppGisTriageData {
  id: number;
  confirmedPhone?: string;
  confirmedEmail?: string;
  // Null for no-order GIS sessions (patients with no backing test order).
  orderUidPublic?: string | null;
  testUid?: string | null;
  // Entry point that launched the GIS funnel (e.g. "testResultDetailPage",
  // "Public_Entry_UPP_Auth", "Public_Entry_Order_Lookup").
  schedulingSource?: string | null;
  // Pre/post-test classification of the order at session creation.
  resultStatus?: string | null;
  prefilledData?: IntakeFormData;
  userInputFields?: IntakeFormUserInputFields;
  triageResult?: TriageResult;
  bookingConfirmed?: string | null;
  gisScheduleClicked: string;
  intakeStarted?: string | null;
  intakeCompleted?: string | null;
}

export interface CreateOncUppGisTriageResponse {
  createOncUppGisTriage: OncUppGisTriageData;
}

export interface GetActiveOncUppGisTriageResponse {
  getActiveOncUppGisTriage: OncUppGisTriageData | null;
}

export interface UpdateOncUppGisTriageStepResponse {
  updateOncUppGisTriageStep: OncUppGisTriageData;
}

export interface CompleteOncUppGisTriageResponse {
  completeOncUppGisTriage: OncUppGisTriageData;
}

export interface ConfirmOncUppGisTriageBookingResponse {
  confirmOncUppGisTriageBooking: OncUppGisTriageData;
}

export interface MarkOncUppGisTriageIntakeStartedResponse {
  markOncUppGisTriageIntakeStarted: OncUppGisTriageData;
}

export interface MarkOncUppGisTriageIntakeCompletedResponse {
  markOncUppGisTriageIntakeCompleted: OncUppGisTriageData;
}

export interface PreviewOncUppGisTriageResponse {
  previewOncUppGisTriage: TriageResult;
}

export interface UpdateSessionStepInput {
  hasActiveDisease?: boolean;
  reasonForAppointment?: string;
  easeOfUnderstandingInformation?: string;
  wantToTalkToGeneticCounselorImmediately?: boolean;
  alreadyScheduledAppointmentWithDoctor?: boolean;
  countryOfResidence?: string;
  interpreterNeeded?: boolean;
  interpreterLanguage?: string;
  consentAcknowledged?: boolean;
}

/**
 * Partial patch for values collected by dedicated intake steps and persisted
 * alongside the session's server-provided prefilled data.
 */
export interface UpdatePrefilledDataInput {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  zipCode?: string;
  cancerType?: string;
  cancerStage?: CancerStage;
  physicianClinic?: string;
  orderingPhysician?: string;
}

/**
 * GraphQL query to fetch pre-filled GIS intake form data from backend.
 * This query aggregates data from multiple sources (patient, profile, test details)
 * and checks for existing GIS appointments.
 */
export const GET_GIS_INTAKE_FORM_DATA_QUERY = gql`
  query GetGisIntakeFormData($orderUidPublic: String!, $testUid: String!) {
    getGisIntakeFormData(orderUidPublic: $orderUidPublic, testUid: $testUid) {
      firstName
      lastName
      dateOfBirth
      phone
      email
      physicianClinic
      orderingPhysician
      cancerType
      cancerStage
      testType
      hasGISAppointment
      hasPreviouslyTakenTest
    }
  }
`;

const GIS_SESSION_FIELDS = `
  id
  confirmedPhone
  confirmedEmail
  orderUidPublic
  testUid
  schedulingSource
  resultStatus
  prefilledData {
    firstName
    lastName
    dateOfBirth
    phone
    email
    zipCode
    physicianClinic
    orderingPhysician
    cancerType
    cancerStage
    testType
    hasGISAppointment
    hasPreviouslyTakenTest
  }
  userInputFields {
    hasActiveDisease
    reasonForAppointment
    easeOfUnderstandingInformation
    wantToTalkToGeneticCounselorImmediately
    alreadyScheduledAppointmentWithDoctor
    countryOfResidence
    interpreterNeeded
    interpreterLanguage
    consentAcknowledged
  }
  triageResult {
    gisFlowType
    skipPatientEducation
    matchedRules {
      ruleId
      description
    }
  }
  bookingConfirmed
  gisScheduleClicked
  intakeStarted
  intakeCompleted
`;

export const CREATE_ONC_UPP_GIS_TRIAGE_MUTATION = gql`
  mutation CreateOncUppGisTriage(
    $orderUidPublic: String!
    $testUid: String!
    $confirmedPhone: String
    $confirmedEmail: String
    $input: UpdateSessionStepInput
    $schedulingSource: String
  ) {
    createOncUppGisTriage(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      confirmedPhone: $confirmedPhone
      confirmedEmail: $confirmedEmail
      input: $input
      schedulingSource: $schedulingSource
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const UPDATE_ONC_UPP_GIS_TRIAGE_STEP_MUTATION = gql`
  mutation UpdateOncUppGisTriageStep(
    $sessionId: Int!
    $input: UpdateSessionStepInput!
    $confirmedPhone: String
    $confirmedEmail: String
    $prefilledInput: UpdatePrefilledDataInput
  ) {
    updateOncUppGisTriageStep(
      sessionId: $sessionId
      input: $input
      confirmedPhone: $confirmedPhone
      confirmedEmail: $confirmedEmail
      prefilledInput: $prefilledInput
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const COMPLETE_ONC_UPP_GIS_TRIAGE_MUTATION = gql`
  mutation CompleteOncUppGisTriage($sessionId: Int!) {
    completeOncUppGisTriage(sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const CONFIRM_ONC_UPP_GIS_TRIAGE_BOOKING_MUTATION = gql`
  mutation ConfirmOncUppGisTriageBooking($sessionId: Int!) {
    confirmOncUppGisTriageBooking(sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const MARK_ONC_UPP_GIS_TRIAGE_INTAKE_STARTED_MUTATION = gql`
  mutation MarkOncUppGisTriageIntakeStarted($sessionId: Int!) {
    markOncUppGisTriageIntakeStarted(sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const MARK_ONC_UPP_GIS_TRIAGE_INTAKE_COMPLETED_MUTATION = gql`
  mutation MarkOncUppGisTriageIntakeCompleted($sessionId: Int!) {
    markOncUppGisTriageIntakeCompleted(sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const GET_ACTIVE_ONC_UPP_GIS_TRIAGE_QUERY = gql`
  query GetActiveOncUppGisTriage(
    $orderUidPublic: String!
    $testUid: String!
  ) {
    getActiveOncUppGisTriage(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

const GIS_TRIAGE_RESULT_FIELDS = `
  gisFlowType
  skipPatientEducation
  matchedRules {
    ruleId
    description
  }
`;

export const PREVIEW_ONC_UPP_GIS_TRIAGE_QUERY = gql`
  query PreviewOncUppGisTriage(
    $orderUidPublic: String!
    $testUid: String!
    $confirmedPhone: String
    $confirmedEmail: String
    $input: UpdateSessionStepInput
  ) {
    previewOncUppGisTriage(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      confirmedPhone: $confirmedPhone
      confirmedEmail: $confirmedEmail
      input: $input
    ) {
      ${GIS_TRIAGE_RESULT_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// NonAuth (guest) GraphQL documents
//
// Token-authorized equivalents used by the anonymous order-lookup
// (Public_Entry_Order_Lookup) GIS funnel. Order-scoped calls bind the signed
// guest token to the requested test; session-scoped calls scope by the token's
// patient uid. See server/src/graphql/resolvers/gisIntakeFormResolver.ts.
// ---------------------------------------------------------------------------

export const GET_GIS_INTAKE_FORM_DATA_NONAUTH_QUERY = gql`
  query GetGisIntakeFormDataNonAuth(
    $token: String!
    $orderUidPublic: String!
    $testUid: String!
  ) {
    getGisIntakeFormDataNonAuth(
      token: $token
      orderUidPublic: $orderUidPublic
      testUid: $testUid
    ) {
      firstName
      lastName
      dateOfBirth
      phone
      email
      physicianClinic
      orderingPhysician
      cancerType
      cancerStage
      testType
      hasGISAppointment
      hasPreviouslyTakenTest
    }
  }
`;

export const GET_ACTIVE_ONC_UPP_GIS_TRIAGE_NONAUTH_QUERY = gql`
  query GetActiveOncUppGisTriageNonAuth(
    $token: String!
    $orderUidPublic: String!
    $testUid: String!
  ) {
    getActiveOncUppGisTriageNonAuth(
      token: $token
      orderUidPublic: $orderUidPublic
      testUid: $testUid
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const UPDATE_ONC_UPP_GIS_TRIAGE_STEP_NONAUTH_MUTATION = gql`
  mutation UpdateOncUppGisTriageStepNonAuth(
    $token: String!
    $sessionId: Int!
    $input: UpdateSessionStepInput!
    $confirmedPhone: String
    $confirmedEmail: String
    $prefilledInput: UpdatePrefilledDataInput
  ) {
    updateOncUppGisTriageStepNonAuth(
      token: $token
      sessionId: $sessionId
      input: $input
      confirmedPhone: $confirmedPhone
      confirmedEmail: $confirmedEmail
      prefilledInput: $prefilledInput
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const COMPLETE_ONC_UPP_GIS_TRIAGE_NONAUTH_MUTATION = gql`
  mutation CompleteOncUppGisTriageNonAuth($token: String!, $sessionId: Int!) {
    completeOncUppGisTriageNonAuth(token: $token, sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const CONFIRM_ONC_UPP_GIS_TRIAGE_BOOKING_NONAUTH_MUTATION = gql`
  mutation ConfirmOncUppGisTriageBookingNonAuth(
    $token: String!
    $sessionId: Int!
  ) {
    confirmOncUppGisTriageBookingNonAuth(token: $token, sessionId: $sessionId) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

export const MARK_ONC_UPP_GIS_TRIAGE_INTAKE_STARTED_NONAUTH_MUTATION = gql`
  mutation MarkOncUppGisTriageIntakeStartedNonAuth(
    $token: String!
    $sessionId: Int!
  ) {
    markOncUppGisTriageIntakeStartedNonAuth(
      token: $token
      sessionId: $sessionId
    ) {
      ${GIS_SESSION_FIELDS}
    }
  }
`;

/**
 * Strips null/undefined values from user input fields for GraphQL submission.
 */
export function toNonNullFields(
  fields: IntakeFormUserInputFields
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value !== null && value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Service for managing GIS (Genetic Information Session) intake form data.
 * Now primarily provides helper methods - data aggregation is handled by the backend.
 */
export default class IntakeFormService {
  /**
   * Returns the GraphQL query for fetching GIS intake form data.
   * Use with Apollo client to fetch pre-filled form data from the backend.
   *
   * @returns DocumentNode for the getGisIntakeFormData query
   */
  public static getGisIntakeFormData(): DocumentNode {
    return GET_GIS_INTAKE_FORM_DATA_QUERY;
  }

  public static getActiveOncUppGisTriage(): DocumentNode {
    return GET_ACTIVE_ONC_UPP_GIS_TRIAGE_QUERY;
  }

  public static previewOncUppGisTriage(): DocumentNode {
    return PREVIEW_ONC_UPP_GIS_TRIAGE_QUERY;
  }

  public static createOncUppGisTriage(): DocumentNode {
    return CREATE_ONC_UPP_GIS_TRIAGE_MUTATION;
  }

  public static updateOncUppGisTriageStep(): DocumentNode {
    return UPDATE_ONC_UPP_GIS_TRIAGE_STEP_MUTATION;
  }

  public static completeOncUppGisTriage(): DocumentNode {
    return COMPLETE_ONC_UPP_GIS_TRIAGE_MUTATION;
  }

  public static confirmOncUppGisTriageBooking(): DocumentNode {
    return CONFIRM_ONC_UPP_GIS_TRIAGE_BOOKING_MUTATION;
  }

  public static markOncUppGisTriageIntakeStarted(): DocumentNode {
    return MARK_ONC_UPP_GIS_TRIAGE_INTAKE_STARTED_MUTATION;
  }

  public static markOncUppGisTriageIntakeCompleted(): DocumentNode {
    return MARK_ONC_UPP_GIS_TRIAGE_INTAKE_COMPLETED_MUTATION;
  }

  public static getGisIntakeFormDataNonAuth(): DocumentNode {
    return GET_GIS_INTAKE_FORM_DATA_NONAUTH_QUERY;
  }

  public static getActiveOncUppGisTriageNonAuth(): DocumentNode {
    return GET_ACTIVE_ONC_UPP_GIS_TRIAGE_NONAUTH_QUERY;
  }

  public static updateOncUppGisTriageStepNonAuth(): DocumentNode {
    return UPDATE_ONC_UPP_GIS_TRIAGE_STEP_NONAUTH_MUTATION;
  }

  public static completeOncUppGisTriageNonAuth(): DocumentNode {
    return COMPLETE_ONC_UPP_GIS_TRIAGE_NONAUTH_MUTATION;
  }

  public static confirmOncUppGisTriageBookingNonAuth(): DocumentNode {
    return CONFIRM_ONC_UPP_GIS_TRIAGE_BOOKING_NONAUTH_MUTATION;
  }

  public static markOncUppGisTriageIntakeStartedNonAuth(): DocumentNode {
    return MARK_ONC_UPP_GIS_TRIAGE_INTAKE_STARTED_NONAUTH_MUTATION;
  }

  /**
   * Creates an initial user input fields object with all null values.
   * Use this as the starting state for user-provided form fields.
   *
   * @returns IntakeFormUserInputFields with all fields set to null
   */
  public static createEmptyUserInputFields(): IntakeFormUserInputFields {
    return {
      hasActiveDisease: null,
      reasonForAppointment: null,
      easeOfUnderstandingInformation: null,
      wantToTalkToGeneticCounselorImmediately: null,
      alreadyScheduledAppointmentWithDoctor: null,
      countryOfResidence: null,
      interpreterNeeded: null,
      interpreterLanguage: null,
      consentAcknowledged: null,
    };
  }

  /**
   * Combines pre-filled data with user input to create complete intake form data.
   *
   * @param prefilledData - Data aggregated from existing sources
   * @param userInput - Data provided by the user during form flow
   * @returns CompleteIntakeFormData ready for submission
   */
  public static combineIntakeFormData(
    prefilledData: IntakeFormData,
    userInput: IntakeFormUserInputFields
  ): CompleteIntakeFormData {
    return {
      ...prefilledData,
      ...userInput,
    };
  }

  /**
   * Checks if the intake form data has minimum required contact info.
   *
   * @param data - The intake form data to validate
   * @returns boolean indicating if phone and email are present
   */
  public static hasRequiredContactInfo(data: IntakeFormData): boolean {
    return Boolean(data.phone && data.email);
  }

  /**
   * Checks if the test is an oncology test that requires cancer-related questions.
   *
   * @param testType - The test type from the order
   * @returns boolean indicating if cancer questions should be shown
   */
  public static isOncologyTest(testType: string | null): boolean {
    if (!testType) return false;
    const oncologyTestTypes = ["SIGNATERA", "ALTERA", "LATITUDE"];
    return oncologyTestTypes.includes(testType.toUpperCase());
  }
}
